import { useEffect, useRef, useState } from "react";
import { renderPoster, uploadSnapshot } from "@/lib/renderPoster";
import { PosterContent } from "@/components/PosterCanvas";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Mails, Loader2, Send } from "lucide-react";

const A4_H = Math.round(1280 / Math.SQRT2);
const splitEmails = (txt) => txt.split(/[\s,;]+/).map((e) => e.trim()).filter(Boolean);

const defaultSelection = (list) => {
  const latest = list.filter((p) => p.split_of).sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || ""))[0];
  return latest ? list.filter((p) => p.split_of === latest.split_of).map((p) => p.id) : [];
};

const Offscreen = ({ poster, tvRefs, a4Refs }) => (
  <>
    <div ref={(n) => (tvRefs.current[poster.id] = n)} style={{ width: 1280, height: 720 }}><PosterContent data={poster} editable={false} /></div>
    <div ref={(n) => (a4Refs.current[poster.id] = n)} style={{ width: 1280, height: A4_H }}><PosterContent data={poster} editable={false} height={A4_H} /></div>
  </>
);

const KIND = { senior: "Seniors", academy: "Académie" };

const Thumb = ({ src, name, kind, id }) => (
  <div className="space-y-1" data-testid={`group-thumb-${id}`}>
    <div className="overflow-hidden rounded-md border border-border bg-muted" style={{ aspectRatio: "1.414" }}>
      {src && src !== "error" ? <img src={src} alt={name} className="h-full w-full object-contain" />
        : <div className="flex h-full items-center justify-center text-[10px] text-muted-foreground">{src === "error" ? "Aperçu indisponible" : <Loader2 className="h-4 w-4 animate-spin" />}</div>}
    </div>
    <p className="truncate text-[11px] text-muted-foreground" title={name}>
      {KIND[kind] && <span className="mr-1 font-semibold text-foreground">{KIND[kind]}</span>}{name}
    </p>
  </div>
);

export const GroupSendDialog = () => {
  const [open, setOpen] = useState(false);
  const [posters, setPosters] = useState([]);
  const [selected, setSelected] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [extra, setExtra] = useState("");
  const [busy, setBusy] = useState(false);
  const [thumbs, setThumbs] = useState({});
  const tvRefs = useRef({});
  const a4Refs = useRef({});
  const chosen = posters.filter((p) => selected.includes(p.id));

  const onOpen = async (o) => {
    setOpen(o);
    if (!o) return;
    setExtra("");
    setThumbs({});
    const [{ data: list }, { data: rec }] = await Promise.all([api.get("/posters"), api.get("/poster-recipients")]);
    const usc = list.filter((p) => p.layout === "usc");
    setPosters(usc);
    setSelected(defaultSelection(usc));
    setRecipients(rec.emails);
  };

  useEffect(() => {
    if (!open) return;
    let live = true;
    const t = setTimeout(async () => {
      for (const id of selected) {
        const node = a4Refs.current[id];
        if (!node || !live) continue;
        const src = await renderPoster(node, { pixelRatio: 0.3, width: 1280, height: A4_H, backgroundColor: "#0b1a3a", cacheBust: true })
          .then((c) => c.toDataURL("image/jpeg", 0.8)).catch(() => "error");
        if (live) setThumbs((m) => ({ ...m, [id]: src }));
      }
    }, 300);
    return () => { live = false; clearTimeout(t); };
  }, [open, selected]);

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id].slice(0, 6)));

  const send = async () => {
    setBusy(true);
    try {
      for (const p of chosen) {
        await uploadSnapshot(api, p.id, tvRefs.current[p.id], 720, "tv");
        await uploadSnapshot(api, p.id, a4Refs.current[p.id], A4_H, "a4");
      }
      const { data } = await api.post("/posters/send-mail-group", { poster_ids: selected, extra: splitEmails(extra) });
      toast.success(`${chosen.length} affiche(s) envoyée(s) à : ${data.sent_to.join(", ")}`);
      setOpen(false);
    } catch (e) {
      toast.error(e.response ? formatApiErrorDetail(e.response.data?.detail) : "Impossible de préparer les images. Rechargez la page puis réessayez.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpen}>
      <DialogTrigger asChild>
        <Button variant="secondary" data-testid="group-send-button"><Mails className="mr-1 h-4 w-4" /> Envoi groupé</Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl" data-testid="group-send-dialog">
        <DialogHeader>
          <DialogTitle>Envoi groupé</DialogTitle>
          <DialogDescription>Un seul mail avec une image A4 par affiche cochée (6 maximum).</DialogDescription>
        </DialogHeader>
        <div className="max-h-44 space-y-1.5 overflow-y-auto rounded-lg border border-border p-2" data-testid="group-send-list">
          {posters.map((p) => (
            <label key={p.id} className="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 text-sm hover:bg-secondary">
              <Checkbox checked={selected.includes(p.id)} onCheckedChange={() => toggle(p.id)} data-testid={`group-send-check-${p.id}`} />
              {KIND[p.split_kind] && <span className="rounded bg-primary/15 px-1.5 text-[10px] font-semibold text-primary">{KIND[p.split_kind]}</span>}
              <span className="truncate" title={p.name}>{p.name}</span>
            </label>
          ))}
        </div>
        <div style={{ position: "fixed", left: -20000, top: 0, width: 1280, pointerEvents: "none", zIndex: -1 }} aria-hidden="true">
          {chosen.map((p) => <Offscreen key={p.id} poster={p} tvRefs={tvRefs} a4Refs={a4Refs} />)}
        </div>
        {chosen.length > 0 && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="group-send-thumbs">
            {chosen.map((p) => <Thumb key={p.id} id={p.id} src={thumbs[p.id]} name={p.name} kind={p.split_kind} />)}
          </div>
        )}
        <div className="space-y-1">
          <Label className="text-xs">Destinataires enregistrés</Label>
          <p className="text-sm" data-testid="group-send-recipients">
            {recipients.length ? recipients.join(", ") : <span className="text-muted-foreground">Aucun (à régler dans Administration)</span>}
          </p>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Ajouter une adresse pour cet envoi (facultatif)</Label>
          <Input value={extra} onChange={(e) => setExtra(e.target.value)} placeholder="ex. secretaire@club.be" data-testid="group-send-extra-input" />
        </div>
        <DialogFooter>
          <Button onClick={send} disabled={busy || !chosen.length || (!recipients.length && !splitEmails(extra).length)} data-testid="group-send-confirm">
            {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Send className="mr-1 h-4 w-4" />} Envoyer {chosen.length} affiche(s)
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
